import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, 
  LogOut, 
  User, 
  Shield, 
  Menu, 
  X, 
  MessageSquare,
  Settings, 
  Layers,   
  CreditCard, 
  Lock      
} from 'lucide-react';
import { useAuth } from '../../authentication_components/authContext';

export default function HeaderBar({ activeTab, onTabChange }) {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [manageDropdownOpen, setManageDropdownOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  
  // Refs for click outside handling
  const manageDropdownRef = useRef(null);
  
  const { user, loginType, logout } = useAuth();
  const navigate = useNavigate();
  
  const navToPortal = () => navigate('/portal');

  // Navigation items
  const navItems = [
    { id: 'contact-submissions', label: 'Contacto', icon: MessageSquare },
    { id: 'search', label: 'Buscar Negocios' },
    { 
      id: 'management-group', 
      label: 'Gestión', 
      icon: Settings,
      children: [
        { id: 'content', label: 'Contenido', icon: Layers },
        { id: 'subscriptions', label: 'Suscripciones', icon: CreditCard },
        { id: 'access', label: 'Acceso', icon: Lock },
      ]
    },
    { id: 'register', label: 'Registrar Negocio' },
    ...(loginType === 'master' ? [
      {
        id: 'admin-registration',
        label: 'Registrar Admin',
        isMasterOnly: true
      },
      {
        id: 'admin-approval',
        label: 'Aprobar Admins',
        isMasterOnly: true
      },
      {
        id: 'qr-scans',
        label: 'QR scans',
        isMasterOnly: true,
      },
    ] : [])
  ];

  // =========================================================
  // FIXED CLICK OUTSIDE HANDLER
  // =========================================================
  useEffect(() => {
    function handleClickOutside(event) {
      // 1. If the mobile menu is open, do NOT run this logic.
      // Mobile users need to actually click the buttons, and since the 
      // buttons are outside the desktop 'manageDropdownRef', 
      // this logic would incorrectly close the menu on 'mousedown'.
      if (mobileMenuOpen) return;

      if (manageDropdownRef.current && !manageDropdownRef.current.contains(event.target)) {
        setManageDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]); // Added mobileMenuOpen as a dependency

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setUserDropdownOpen(false);
      setMobileMenuOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  const getUserDisplayInfo = () => {
    if (!user) return { email: 'Usuario', lastLogin: 'N/A' };
    const email = loginType === 'master' ? 'Master User' : user.email || 'Admin User';
    const lastLogin = user.expiresAt
      ? new Date(user.expiresAt * 1000).toLocaleDateString('es-ES', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }) : 'Sesión actual';
    return { email, lastLogin };
  };

  const { email, lastLogin } = getUserDisplayInfo();

  const getUserIcon = () => {
    if (loginType === 'master') {
      return (
        <div className="w-8 h-8 bg-gradient-to-r from-red-600 to-red-800 rounded-full flex items-center justify-center flex-shrink-0">
          <Shield className="h-4 w-4 text-white" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 bg-gradient-to-r from-black to-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
        <User className="h-4 w-4 text-white" />
      </div>
    );
  };

  const getUserTypeLabel = () => {
    return loginType === 'master' ? 'Master Administrator' : 'Administrator';
  };

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    setMobileMenuOpen(false);
    setManageDropdownOpen(false);
  };

  const getMasterStatusIcon = (item) => {
    if (!item.isMasterOnly) return null;
    return <Shield className="h-3 w-3 sm:h-4 sm:w-4 ml-1.5" />;
  };

  const isGroupActive = (item) => {
    return item.children && item.children.some(child => child.id === activeTab);
  };

  return (
    <header className="bg-white shadow-lg border-b border-orange-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <div className="flex flex-start items-start min-w-0 flex-shrink-0">
            <h1
              onClick={navToPortal}
              className="text-lg cursor-pointer sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent truncate">
              JaydevsPR
            </h1>
            {loginType === 'master' && (
              <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-100 text-red-800 text-[10px] sm:text-xs font-medium rounded-full border border-red-200 whitespace-nowrap">
                MASTER
              </span>
            )}
          </div>

          {/* Desktop Navigation Tabs */}
          <div className="hidden lg:flex space-x-1 flex-1 justify-center mx-4">
            {navItems.map((item) => {
              const ItemIcon = item.icon;
              
              if (item.children) {
                const isActive = isGroupActive(item);
                return (
                  <div key={item.id} className="relative" ref={manageDropdownRef}>
                    <button
                      onClick={() => setManageDropdownOpen(!manageDropdownOpen)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center whitespace-nowrap ${
                        isActive
                          ? 'bg-orange-100 text-orange-800 border border-orange-300'
                          : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      {ItemIcon && <ItemIcon className="h-4 w-4 mr-1.5" />}
                      <span>{item.label}</span>
                      <ChevronDown className={`h-3 w-3 ml-1.5 transition-transform duration-200 ${manageDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {manageDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-orange-200 py-1 z-50">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = activeTab === child.id;
                          return (
                            <button
                              key={child.id}
                              onClick={() => handleTabChange(child.id)}
                              className={`w-full text-left px-4 py-2 text-sm flex items-center transition-colors ${
                                isChildActive 
                                  ? 'bg-orange-50 text-orange-700 font-medium' 
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-orange-600'
                              }`}
                            >
                              {ChildIcon && <ChildIcon className="h-4 w-4 mr-2 opacity-70" />}
                              {child.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center whitespace-nowrap ${
                    activeTab === item.id
                      ? item.isMasterOnly
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-orange-100 text-orange-800 border border-orange-300'
                      : item.isMasterOnly
                        ? 'text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200'
                        : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {ItemIcon && <ItemIcon className="h-4 w-4 mr-1.5" />}
                  <span>{item.label}</span>
                  {getMasterStatusIcon(item)}
                </button>
              );
            })}
          </div>

          {/* User Section & Mobile Menu Button */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 ml-auto">
            {/* User Info & Dropdown (Kept same as original) */}
            <div className="hidden lg:block text-right text-sm">
              <div className="flex items-center justify-end space-x-1">
                <p className={`font-medium whitespace-nowrap ${loginType === 'master' ? 'text-red-800' : 'text-gray-800'}`}>
                  {email}
                </p>
                {loginType === 'master' && <Shield className="h-3 w-3 text-red-500 flex-shrink-0" />}
              </div>
              <p className="text-gray-500 text-xs whitespace-nowrap">{getUserTypeLabel()}</p>
              <p className="text-gray-500 text-xs whitespace-nowrap">Último acceso: {lastLogin}</p>
            </div>

            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                disabled={loggingOut}
                className={`flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 rounded-lg transition-colors ${
                  loggingOut ? 'opacity-50 cursor-not-allowed' : loginType === 'master' ? 'hover:bg-red-50' : 'hover:bg-orange-50'
                }`}
              >
                {getUserIcon()}
                <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${userDropdownOpen ? 'rotate-180' : ''} ${loginType === 'master' ? 'text-red-500' : 'text-gray-500'}`} />
              </button>

              {userDropdownOpen && !loggingOut && (
                <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-orange-200 z-50">
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center space-x-2">
                      {loginType === 'master' ? <Shield className="h-4 w-4 text-red-500 flex-shrink-0" /> : <User className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{email}</p>
                        <p className="text-xs text-gray-500">{getUserTypeLabel()}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={handleLogout}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        loginType === 'master' ? 'text-red-700 hover:bg-red-50 hover:text-red-800' : 'text-gray-700 hover:bg-orange-50 hover:text-orange-800'
                      }`}
                    >
                      <LogOut className={`h-4 w-4 mr-2 flex-shrink-0 ${loginType === 'master' ? 'text-red-500' : 'text-orange-500'}`} />
                      <span className="truncate">Cerrar Sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${loginType === 'master' ? 'hover:bg-red-50' : 'hover:bg-orange-50'}`}
            >
              {mobileMenuOpen ? <X className="h-6 w-6 text-gray-600" /> : <Menu className="h-6 w-6 text-gray-600" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-3 border-t border-orange-200">
            <div className="space-y-1">
              {navItems.map((item) => {
                const ItemIcon = item.icon;
                
                if (item.children) {
                  const isActive = isGroupActive(item);
                  return (
                    <div key={item.id} className="bg-gray-50 rounded-lg overflow-hidden mb-1">
                      <button
                        onClick={() => setManageDropdownOpen(!manageDropdownOpen)}
                        className={`w-full text-left px-3 py-2.5 text-sm font-medium flex items-center justify-between ${
                          isActive ? 'text-orange-800 bg-orange-100' : 'text-gray-700'
                        }`}
                      >
                        <div className="flex items-center">
                          {ItemIcon && <ItemIcon className="h-4 w-4 mr-2" />}
                          <span>{item.label}</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-transform ${manageDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {manageDropdownOpen && (
                        <div className="pl-4 pr-2 pb-2 space-y-1 bg-white border-t border-gray-100">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            return (
                              <button
                                key={child.id}
                                onClick={() => handleTabChange(child.id)}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center ${
                                  activeTab === child.id
                                    ? 'bg-orange-50 text-orange-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {ChildIcon && <ChildIcon className="h-3.5 w-3.5 mr-2 opacity-70" />}
                                {child.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-between ${
                      activeTab === item.id
                        ? item.isMasterOnly
                          ? 'bg-red-100 text-red-800 border border-red-300'
                          : 'bg-orange-100 text-orange-800 border border-orange-300'
                        : item.isMasterOnly
                          ? 'text-red-600 hover:text-red-700 hover:bg-red-50 border border-transparent'
                          : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center">
                      {ItemIcon && <ItemIcon className="h-4 w-4 mr-2" />}
                      <span>{item.label}</span>
                    </div>
                    {getMasterStatusIcon(item)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}